import { useState } from "react";
import type { LightingOverrides } from "./SinglePanScene";

interface SliderRowProps {
  label: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange: (v: number) => void;
}

function SliderRow({ label, value, min = 0, max = 5, step = 0.1, onChange }: SliderRowProps) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between text-[10px] tracking-wider uppercase">
        <span className="text-white/50">{label}</span>
        <span className="text-white/70 tabular-nums">{value.toFixed(1)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-1 appearance-none bg-white/10 rounded-full outline-none cursor-pointer
          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
          [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white/80
          [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:rounded-full
          [&::-moz-range-thumb]:bg-white/80 [&::-moz-range-thumb]:border-0"
      />
    </div>
  );
}

interface LightingPanelProps {
  lighting: LightingOverrides;
  onChange: (l: LightingOverrides) => void;
  defaults: LightingOverrides;
}

export default function LightingPanel({ lighting, onChange, defaults }: LightingPanelProps) {
  const [open, setOpen] = useState(false);

  const update = (key: keyof LightingOverrides, value: number) => {
    onChange({ ...lighting, [key]: value });
  };

  const reset = () => onChange({ ...defaults });

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        title="Tweak lighting"
        className="w-10 h-10 rounded-full flex items-center justify-center bg-white/5 text-white/50 hover:text-white hover:bg-white/10 border border-white/10 backdrop-blur-md transition-all duration-200 text-lg"
      >
        ☀
      </button>
    );
  }

  return (
    <div className="w-56 rounded-xl bg-black/80 backdrop-blur-xl border border-white/10 p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-[10px] tracking-[0.2em] uppercase text-white/60">Lighting</span>
        <div className="flex gap-2">
          <button onClick={reset} className="text-[9px] tracking-wider uppercase text-white/30 hover:text-white/60 transition-colors">Reset</button>
          <button onClick={() => setOpen(false)} className="text-white/30 hover:text-white/60 transition-colors text-sm">✕</button>
        </div>
      </div>
      <SliderRow label="Ambient" value={lighting.ambient ?? 0.5} onChange={(v) => update("ambient", v)} max={8} />
      <SliderRow label="Front Spot" value={lighting.spotFront ?? 2.0} onChange={(v) => update("spotFront", v)} max={15} />
      <SliderRow label="Back Spot" value={lighting.spotBack ?? 1.5} onChange={(v) => update("spotBack", v)} max={15} />
      <SliderRow label="Fill Light" value={lighting.pointFill ?? 0.8} onChange={(v) => update("pointFill", v)} max={10} />
      <SliderRow label="Environment" value={lighting.envIntensity ?? 2.2} onChange={(v) => update("envIntensity", v)} max={8} />
    </div>
  );
}
